import { useState, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useBranchContext } from "@/hooks/useBranchContext";

interface ActivityUpdate {
  activityId: string;
  patientId: string;
  updates: {
    status?: string;
    completed_by?: string;
    completion_time?: string;
    time_taken_minutes?: number;
    parallel_activities?: any;
    started_at?: string;
  };
}

export const useActivityUpdate = () => {
  const queryClient = useQueryClient();
  const [isUpdating, setIsUpdating] = useState(false);
  const { currentBranchId } = useBranchContext();

  const updateActivity = useCallback(async ({ activityId, patientId, updates }: ActivityUpdate) => {
    try {
      setIsUpdating(true);
      console.log('🔄 Updating activity:', { activityId, updates });
      if (currentBranchId == null) {
        toast.error("Branch not selected");
        return { success: false, error: new Error("Branch not selected") };
      }

      // The database trigger will auto-set completion_time and time_taken_minutes
      // It will also check if all parallel activities are completed and start the next one
      const { error } = await supabase
        .from('activities')
        .update(updates)
        .eq('id', activityId)
        .eq('branch_id', currentBranchId);

      if (error) throw error;

      // Add a small delay to ensure triggers have fired
      await new Promise(resolve => setTimeout(resolve, 100));

      // Invalidate all relevant queries
      await queryClient.invalidateQueries({ 
        queryKey: ['activities', currentBranchId, patientId] 
      });
      await queryClient.invalidateQueries({ 
        queryKey: ['patients', currentBranchId] 
      });
      await queryClient.invalidateQueries({ 
        queryKey: ['patient', currentBranchId, patientId] 
      });

      toast.success("Activity updated successfully");
      return { success: true };
    } catch (error) {
      console.error('❌ Error updating activity:', error);
      toast.error("Failed to update activity");
      return { success: false, error };
    } finally {
      setIsUpdating(false);
    }
  }, [queryClient, currentBranchId]);

  return {
    updateActivity,
    isUpdating,
  };
};
